import React, { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { getPSWPResponsiblePersons, getPSWPSignatureById } from '../../services/plantSafetyWorkPermitService';

interface Props {
  signatureId?: number | null;
  workPermitId?: number | null;
  initialValues?: {
    name: string;
    responsiblePerson: string;
  };
}

const PlantPermitSignature = forwardRef((props: Props, ref) => {
  const { signatureId, initialValues } = props;

  const [responsiblePersons, setResponsiblePersons] = useState<{ value: string; label: string }[]>([]);
  const [personsLoaded, setPersonsLoaded] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      user_id: '',
    },
    validate: (values) => {
      const errors: Partial<typeof values> = {};
      if (!values.name) errors.name = 'Name is required';
      if (!values.user_id) errors.user_id = 'Responsible Person is required';
      return errors;
    },
    onSubmit: () => {},
  });

  //  expose methods to parent
  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const errors = await formik.validateForm();
      formik.setTouched({
        name: true,
        user_id: true,
      });
      return Object.keys(errors).length === 0;
    },
    getValues: () => ({
      name: formik.values.name,
      user_id: Number(formik.values.user_id),
    }),
    setValues: (values: any) => {
      formik.setValues({
        name: values.name || '',
        user_id: values.user_id ? String(values.user_id) : '',
      });
    },
  }));

  useEffect(() => {
    const fetchResponsiblePersons = async () => {
      try {
        const response = await getPSWPResponsiblePersons();

        const data = Array.isArray(response) ? response : response?.data || [];

        const mappedData = data.map((item: any) => ({
          value: String(item.id),
          label: item.name,
        }));

        setResponsiblePersons(mappedData);
        setPersonsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch responsible persons', error);
      }
    };

    fetchResponsiblePersons();
  }, []);

  // Keep the signature form in sync with the values loaded by the parent in edit mode.
  useEffect(() => {
    if (!initialValues) return;

    formik.setValues({
      name: initialValues.name || '',
      user_id: initialValues.responsiblePerson ? String(initialValues.responsiblePerson) : '',
    });
  }, [initialValues]);

  // FETCH SIGNATURE DATA (EDIT MODE ONLY — only runs when signatureId is a valid number)
  useEffect(() => {
    if (!personsLoaded) return;
    // ── FIX: skip fetch entirely if no signatureId (new permit = blank form) ──
    if (!signatureId) return;

    const fetchSignature = async () => {
      try {
        const res = await getPSWPSignatureById(signatureId);
        const data = res?.data || res;

        if (!data) return;

        const userId = data.user?.id ?? data.user_id ?? data.responsible_person?.id;

        formik.setValues({
          name: data.name || '',
          user_id: userId ? String(userId) : '',
        });
      } catch (err) {
        console.error('Signature load failed', err);
      }
    };

    fetchSignature();
  }, [signatureId, personsLoaded]);

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6 px-2">
      <div className="grid grid-cols-2 gap-6">
        {/* NAME */}
        <div className="flex flex-col">
          <label className="text-black-800 font-semibold text-sm mb-2">
            Name
            <span className="text-red-500">*</span>
          </label>
          <input
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter Name"
            className="w-full bg-gray-100 border border-gray-300 rounded-md px-4 py-3 text-sm 
             focus:outline-none focus:ring-0 focus:border-gray-300"
          />
          {formik.touched.name && formik.errors.name && <p className="text-red-500 text-sm mt-1">{formik.errors.name}</p>}
        </div>

        {/* RESPONSIBLE PERSON */}
        <div className="flex flex-col">
          <label className="text-black-800 font-semibold text-sm mb-2">
            Responsible Person
            <span className="text-red-500">*</span>
          </label>
          <select
            name="user_id"
            value={formik.values.user_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full bg-gray-100 border border-gray-300 rounded-md px-4 py-3 text-sm 
             focus:outline-none focus:ring-0 focus:border-gray-300"
          >
            <option value="">Select Responsible Person</option>
            {responsiblePersons.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {formik.touched.user_id && formik.errors.user_id && <p className="text-red-500 text-sm mt-1">{formik.errors.user_id}</p>}
        </div>
      </div>
    </form>
  );
});

PlantPermitSignature.displayName = 'PlantPermitSignature';
export default PlantPermitSignature;
